import React, { useState, useEffect } from 'react';
import { ref, uploadBytes } from 'firebase/storage';
import { storage, submitAnonymousQuote } from './firebase';
import { getAllSoftware } from './services/db';
import { trackQuoteUploadStarted, trackQuoteSubmitted } from './services/analytics';
import { X, Upload, FileText, Loader, Check } from 'lucide-react';
import './QuoteUpload.css';

const QuoteUpload = ({ isOpen, onClose }) => {
  const [software, setSoftware] = useState([]);
  const [selectedSoftware, setSelectedSoftware] = useState('');
  const [file, setFile] = useState(null);
  const [quoteData, setQuoteData] = useState({
    totalPrice: '',
    seats: '',
    term: '',
    tier: '',
    addOn: '',
    quoteDate: '',
    notes: ''
  });
  const [tierCustom, setTierCustom] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingSoftware, setLoadingSoftware] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Load software list on mount
  useEffect(() => {
    const loadSoftware = async () => {
      try {
        const sw = await getAllSoftware();
        sw.sort((a, b) => a.name.localeCompare(b.name));
        setSoftware(sw);
      } catch (err) {
        console.error('Error loading software:', err);
        setError('Failed to load software list');
      } finally {
        setLoadingSoftware(false);
      }
    };
    if (isOpen) {
      loadSoftware();
      trackQuoteUploadStarted();
    }
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedSoftware('');
      setFile(null);
      setQuoteData({
        totalPrice: '',
        seats: '',
        term: '',
        tier: '',
        addOn: '',
        quoteDate: '',
        notes: ''
      });
      setTierCustom('');
      setFieldErrors({});
      setError('');
      setSuccess(false);
    }
  }, [isOpen]);

  // Derived: the full software object matching the current selection
  const selectedSoftwareData = software.find(sw => sw.id === selectedSoftware) || null;
  const softwareTiers = selectedSoftwareData?.tiers || [];
  const softwareAddOns = selectedSoftwareData?.addOns || [];

  const handleSoftwareChange = (e) => {
    setSelectedSoftware(e.target.value);
    setQuoteData(prev => ({ ...prev, tier: '', addOn: '' }));
    setTierCustom('');
  };

  const validate = (data, customTier) => {
    const errors = {};
    if (data.totalPrice !== '') {
      const price = parseFloat(data.totalPrice);
      if (isNaN(price) || price <= 0) errors.totalPrice = 'Must be greater than $0';
      else if (price >= 10000000) errors.totalPrice = 'Must be less than $10,000,000';
    }
    if (data.seats !== '') {
      const seats = Number(data.seats);
      if (!Number.isInteger(seats) || seats <= 0) errors.seats = 'Must be a whole number greater than 0';
      else if (seats >= 100000) errors.seats = 'Must be less than 100,000';
    }
    if (data.quoteDate !== '') {
      const date = new Date(data.quoteDate);
      const now = new Date();
      const threeYearsAgo = new Date();
      threeYearsAgo.setFullYear(now.getFullYear() - 3);
      if (date > now) errors.quoteDate = 'Cannot be in the future';
      else if (date < threeYearsAgo) errors.quoteDate = 'Must be within the last 3 years';
    }
    if (data.tier === 'Other / Custom' && !customTier.trim()) errors.tier = 'Please enter a tier name';
    if (data.notes.length > 500) errors.notes = `Max 500 characters (${data.notes.length}/500)`;
    return errors;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/msword',
        'application/vnd.ms-excel'
      ];

      if (!allowedTypes.includes(selectedFile.type)) {
        setError('Please upload a PDF, Word document, or Excel file');
        return;
      }

      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      setFile(selectedFile);
      setError('');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...quoteData, [name]: value };
    setQuoteData(updated);
    setFieldErrors(validate(updated, tierCustom));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedSoftware) {
      setError('Please select a software');
      return;
    }
    if (!file) {
      setError('Please upload a quote file');
      return;
    }

    // Final validation pass
    const errors = validate(quoteData, tierCustom);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);

    try {
      const fileExt = file.name.split('.').pop();
      const uniqueId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const filePath = `quotes/${uniqueId}.${fileExt}`;

      const storageRef = ref(storage, filePath);
      await uploadBytes(storageRef, file);

      await submitAnonymousQuote({
        softwareId: selectedSoftware,
        filePath,
        quoteData: {
          totalPrice: quoteData.totalPrice ? parseFloat(quoteData.totalPrice) : null,
          seats: quoteData.seats ? parseInt(quoteData.seats, 10) : null,
          term: quoteData.term || null,
          tier: quoteData.tier === 'Other / Custom' ? (tierCustom.trim() || null) : (quoteData.tier || null),
          addOn: quoteData.addOn || null,
          quoteDate: quoteData.quoteDate || null,
          notes: quoteData.notes || null
        }
      });

      setSuccess(true);
      trackQuoteSubmitted(selectedSoftwareData?.name || 'Unknown');

      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (err) {
      console.error('Error submitting quote:', err);
      setError(err.message || 'Failed to submit quote. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const hasErrors = Object.keys(fieldErrors).length > 0;

  if (!isOpen) return null;

  return (
    <div className="quote-modal-overlay" onClick={onClose}>
      <div className="quote-modal" onClick={e => e.stopPropagation()}>
        <div className="quote-modal-header">
          <h2>Upload Quote</h2>
          <button className="quote-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {success ? (
          <div className="quote-success">
            <Check size={48} />
            <h3>Quote Submitted!</h3>
            <p>Thank you for contributing. Your quote will be reviewed shortly.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="quote-form">
            {error && <div className="quote-error">{error}</div>}

            <div className="quote-form-group">
              <label>Software *</label>
              {loadingSoftware ? (
                <div className="quote-loading-select">Loading software...</div>
              ) : (
                <select
                  value={selectedSoftware}
                  onChange={handleSoftwareChange}
                  disabled={loading}
                >
                  <option value="">Select software...</option>
                  {software.map(sw => (
                    <option key={sw.id} value={sw.id}>{sw.name}</option>
                  ))}
                </select>
              )}
            </div>

            <div className="quote-form-group">
              <label>Quote File *</label>
              <div className="quote-file-upload">
                <input
                  type="file"
                  id="quote-file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  onChange={handleFileChange}
                  disabled={loading}
                />
                <label htmlFor="quote-file" className="quote-file-label">
                  {file ? (
                    <>
                      <FileText size={20} />
                      <span>{file.name}</span>
                    </>
                  ) : (
                    <>
                      <Upload size={20} />
                      <span>Choose file (PDF, Word, Excel)</span>
                    </>
                  )}
                </label>
              </div>
              <p className="quote-file-hint">Max 10MB. Your identity is not recorded.</p>
            </div>

            <div className="quote-form-divider">
              <span>Optional Details</span>
            </div>

            <p className="quote-optional-hint">
              Help us by providing details from the quote. This information will be anonymized and aggregated.
            </p>

            <div className="quote-form-row">
              <div className="quote-form-group">
                <label>Total Price ($)</label>
                <input
                  type="number"
                  name="totalPrice"
                  value={quoteData.totalPrice}
                  onChange={handleInputChange}
                  placeholder="e.g. 5000"
                  disabled={loading}
                  className={fieldErrors.totalPrice ? 'has-error' : ''}
                />
                {fieldErrors.totalPrice && (
                  <span className="quote-field-error">{fieldErrors.totalPrice}</span>
                )}
              </div>
              <div className="quote-form-group">
                <label>Number of Seats</label>
                <input
                  type="number"
                  name="seats"
                  value={quoteData.seats}
                  onChange={handleInputChange}
                  placeholder="e.g. 50"
                  disabled={loading}
                  className={fieldErrors.seats ? 'has-error' : ''}
                />
                {fieldErrors.seats && (
                  <span className="quote-field-error">{fieldErrors.seats}</span>
                )}
              </div>
            </div>

            <div className="quote-form-row">
              <div className="quote-form-group">
                <label>Term</label>
                <select
                  name="term"
                  value={quoteData.term}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="">Select...</option>
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                  <option value="2-year">2 Year</option>
                  <option value="3-year">3 Year</option>
                </select>
              </div>
              <div className="quote-form-group">
                <label>Tier / Plan</label>
                {softwareTiers.length > 0 ? (
                  <>
                    <select
                      name="tier"
                      value={quoteData.tier}
                      onChange={handleInputChange}
                      disabled={loading}
                      className={fieldErrors.tier ? 'has-error' : ''}
                    >
                      <option value="">Select tier...</option>
                      {softwareTiers.map((t, i) => (
                        <option key={i} value={t.name}>{t.name}</option>
                      ))}
                      <option value="Other / Custom">Other / Custom</option>
                    </select>
                    {quoteData.tier === 'Other / Custom' && (
                      <input
                        type="text"
                        value={tierCustom}
                        onChange={e => {
                          setTierCustom(e.target.value);
                          setFieldErrors(validate(quoteData, e.target.value));
                        }}
                        placeholder="Enter tier name..."
                        disabled={loading}
                        className={fieldErrors.tier ? 'has-error' : ''}
                        style={{ marginTop: '8px' }}
                      />
                    )}
                    {fieldErrors.tier && (
                      <span className="quote-field-error">{fieldErrors.tier}</span>
                    )}
                  </>
                ) : (
                  <input
                    type="text"
                    name="tier"
                    value={quoteData.tier}
                    onChange={handleInputChange}
                    placeholder="e.g. Professional, Enterprise"
                    disabled={loading}
                  />
                )}
              </div>
            </div>

            {softwareAddOns.length > 0 && (
              <div className="quote-form-group">
                <label>Add-on (optional)</label>
                <select
                  name="addOn"
                  value={quoteData.addOn}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="">None</option>
                  {softwareAddOns.map((a, i) => (
                    <option key={i} value={a.name}>{a.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="quote-form-group">
              <label>Quote Date</label>
              <input
                type="date"
                name="quoteDate"
                value={quoteData.quoteDate}
                onChange={handleInputChange}
                disabled={loading}
                className={fieldErrors.quoteDate ? 'has-error' : ''}
              />
              {fieldErrors.quoteDate && (
                <span className="quote-field-error">{fieldErrors.quoteDate}</span>
              )}
            </div>

            <div className="quote-form-group">
              <div className="quote-label-row">
                <label>Notes</label>
                <span className={`quote-char-count${quoteData.notes.length > 500 ? ' over-limit' : ''}`}>
                  {quoteData.notes.length}/500
                </span>
              </div>
              <textarea
                name="notes"
                value={quoteData.notes}
                onChange={handleInputChange}
                placeholder="Any additional context about this quote..."
                rows={3}
                disabled={loading}
                className={fieldErrors.notes ? 'has-error' : ''}
              />
              {fieldErrors.notes && (
                <span className="quote-field-error">{fieldErrors.notes}</span>
              )}
            </div>

            <div className="quote-form-actions">
              <button type="button" className="quote-btn-cancel" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="quote-btn-submit" disabled={loading || loadingSoftware || hasErrors}>
                {loading ? (
                  <>
                    <Loader className="spin" size={18} />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    Submit Quote
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default QuoteUpload;
